import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useUser } from '@/hooks/useUser';
import { PageWrapper } from '@/layout/components/PageWrapper';
import { PageHeader } from '@/layout/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ConfirmPopover } from '@/components/ui/ConfirmPopover';
import { BookOpen, Plus, Pencil, Trash2, Search, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useGetDocumentationListQuery, useDeleteDocumentationMutation } from '@/store/documentationApi';

export default function DocumentationListPage() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    const { data: items = [], isLoading } = useGetDocumentationListQuery(undefined, {
        skip: !user?.is_superuser,
    });
    const [deleteDoc] = useDeleteDocumentationMutation();

    const filtered = useMemo(
        () =>
            items.filter((item) =>
                item.title.toLowerCase().includes(search.toLowerCase())
            ),
        [items, search]
    );

    if (!user?.is_superuser) {
        return (
            <PageWrapper>
                <div className="text-center py-20 text-gray-500">
                    <p className="text-xl">גישה מוגבלת לסופר יוזר בלבד</p>
                </div>
            </PageWrapper>
        );
    }

    async function handleDelete(id: number) {
        try {
            await deleteDoc(id).unwrap();
            toast.success('הרשומה נמחקה');
        } catch {
            toast.error('שגיאה במחיקה');
        }
    }

    return (
        <PageWrapper>
            <PageHeader title="תיעוד מערכת" />

            <div className="flex items-center justify-between gap-4 flex-wrap" dir="rtl">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="חיפוש לפי כותרת…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pr-9"
                        dir="rtl"
                    />
                </div>
                <Button onClick={() => navigate('/admin/documentation/new/')} className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    רשומה חדשה
                </Button>
            </div>

            {!isLoading && items.length > 0 && (
                <p className="text-sm text-muted-foreground -mt-2" dir="rtl">
                    {search
                        ? `${filtered.length} תוצאות מתוך ${items.length} רשומות`
                        : `${items.length} רשומות תיעוד`}
                </p>
            )}

            {isLoading ? (
                <div className="flex justify-center py-20">
                    <Spinner />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground rounded-xl border border-dashed">
                    <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p>{search ? 'לא נמצאו תוצאות לחיפוש' : 'אין רשומות תיעוד עדיין'}</p>
                    {!search && (
                        <Button
                            variant="link"
                            onClick={() => navigate('/admin/documentation/new/')}
                            className="mt-1"
                        >
                            צור את הרשומה הראשונה
                        </Button>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2" dir="rtl">
                    {filtered.map((item) => (
                        <div
                            key={item.id}
                            className="group flex items-center justify-between gap-3 rounded-xl border bg-background px-4 py-3.5 shadow-sm hover:shadow-md hover:border-primary/30 transition-all cursor-pointer"
                            onClick={() => navigate(`/admin/documentation/${item.id}/edit/`)}
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                    <BookOpen className="h-4 w-4" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-medium text-foreground truncate group-hover:text-primary transition-colors">
                                        {item.title}
                                    </p>
                                    <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                        <Clock className="h-3 w-3" />
                                        עודכן {new Date(item.updated_at).toLocaleDateString('he-IL')}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => navigate(`/admin/documentation/${item.id}/edit/`)}
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <ConfirmPopover
                                    onConfirm={() => handleDelete(item.id)}
                                    message="האם למחוק את רשומת התיעוד לצמיתות?"
                                >
                                    <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive">
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </ConfirmPopover>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </PageWrapper>
    );
}
